import React, { useCallback, useEffect, useRef, useState } from 'react';
import PlayerAvatar from './PlayerAvatar';
import '../styles/Social.css';

// v640.7: BFS auto-path. Returns array of 'N'/'S'/'E'/'W' or null.
// Avoids tiles with NPCs/characters (except as the goal). Capped to prevent runaway searches.
const AUTOPATH_MAX_LEN = 80;
const AUTOPATH_STEP_MS = 220;
const findAutoPath = (grid, startX, startY, goalX, goalY) => {
  if (!grid || !grid.length) return null;
  const rows = grid.length;
  const cols = grid[0]?.length || 0;
  if (goalX < 0 || goalY < 0 || goalX >= cols || goalY >= rows) return null;
  const goalTile = grid[goalY]?.[goalX];
  if (!goalTile || goalTile.passable === false) return null;
  const key = (x, y) => `${x},${y}`;
  const visited = new Set([key(startX, startY)]);
  const queue = [{ x: startX, y: startY, path: [] }];
  const dirs = [
    [0, -1, 'N'],
    [0, 1, 'S'],
    [-1, 0, 'W'],
    [1, 0, 'E'],
  ];
  while (queue.length) {
    const { x, y, path } = queue.shift();
    if (x === goalX && y === goalY) return path;
    if (path.length >= AUTOPATH_MAX_LEN) continue;
    for (const [ddx, ddy, dr] of dirs) {
      const nx = x + ddx;
      const ny = y + ddy;
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
      const k = key(nx, ny);
      if (visited.has(k)) continue;
      const t = grid[ny]?.[nx];
      if (!t || t.passable === false) continue;
      const isGoal = nx === goalX && ny === goalY;
      // Don't walk through NPCs/characters mid-path
      if (!isGoal && (t.character || t.npc)) continue;
      visited.add(k);
      queue.push({ x: nx, y: ny, path: [...path, dr] });
    }
  }
  return null;
};

const MapGrid = ({ character, maze, nearbyPlayers = [], onNpcClick, onPlayerClick, onMove, viewCols = 5, viewRows = 5, gridLayout = false, showFullMaze = false }) => {
  const mapRef = useRef(null);
  const touchStartRef = useRef(null);
  const swipedRef = useRef(false);
  const [playerSelectPopup, setPlayerSelectPopup] = useState(null); // { players, x, y } for multi-player tile

  // v640.7: Auto-path state
  const autoPathQueueRef = useRef([]);
  const autoPathTimerRef = useRef(null);
  const mazeRef = useRef(maze);
  const characterRef = useRef(character);
  const [autoPathTarget, setAutoPathTarget] = useState(null); // {x, y} for visual marker
  useEffect(() => { mazeRef.current = maze; }, [maze]);
  useEffect(() => { characterRef.current = character; }, [character]);

  // Calculate tile sizes and offsets from viewport dimensions
  const halfCols = Math.floor(viewCols / 2);
  const halfRows = Math.floor(viewRows / 2);
  const tilePctW = 100 / viewCols;
  const tilePctH = 100 / viewRows;

  const cancelAutoPath = useCallback(() => {
    if (autoPathTimerRef.current) {
      clearTimeout(autoPathTimerRef.current);
      autoPathTimerRef.current = null;
    }
    autoPathQueueRef.current = [];
    setAutoPathTarget(null);
  }, []);

  const stepAutoPath = useCallback(() => {
    if (!autoPathQueueRef.current.length) {
      autoPathTimerRef.current = null;
      setAutoPathTarget(null);
      return;
    }
    const dir = autoPathQueueRef.current[0];
    const ch = characterRef.current;
    const m = mazeRef.current;
    if (!ch || !m?.grid) {
      cancelAutoPath();
      return;
    }
    const offsets = { N: [0, -1], S: [0, 1], W: [-1, 0], E: [1, 0] };
    const [ddx, ddy] = offsets[dir] || [0, 0];
    const nx = ch.x_position + ddx;
    const ny = ch.y_position + ddy;
    const tile = m.grid[ny]?.[nx];
    // Re-validate: bail if the next tile became blocked or has a hostile/NPC mid-walk
    if (!tile || tile.passable === false || tile.character || tile.npc) {
      cancelAutoPath();
      return;
    }
    autoPathQueueRef.current.shift();
    if (onMove) onMove(dir);
    if (autoPathQueueRef.current.length) {
      autoPathTimerRef.current = setTimeout(stepAutoPath, AUTOPATH_STEP_MS);
    } else {
      autoPathTimerRef.current = null;
      setAutoPathTarget(null);
    }
  }, [onMove, cancelAutoPath]);

  // Cancel auto-path on any keypress (player intent override) and on unmount
  useEffect(() => {
    const onKey = () => {
      if (autoPathQueueRef.current.length || autoPathTimerRef.current) {
        cancelAutoPath();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (autoPathTimerRef.current) {
        clearTimeout(autoPathTimerRef.current);
        autoPathTimerRef.current = null;
      }
    };
  }, [cancelAutoPath]);

  // Handle tap-to-move: determine direction from tap position relative to player
  const handleTileClick = useCallback((dx, dy, worldX, worldY, tileData) => {
    // If we just handled a swipe, skip this click
    if (swipedRef.current) {
      swipedRef.current = false;
      return;
    }
    if (!onMove) return;
    // Don't move if tapping the player's own tile
    if (dx === 0 && dy === 0) return;

    // Any new click cancels an in-flight auto-path
    if (autoPathQueueRef.current.length || autoPathTimerRef.current) {
      cancelAutoPath();
    }

    const isAdjacent = Math.abs(dx) <= 1 && Math.abs(dy) <= 1;

    if (isAdjacent) {
      // Don't trigger movement if the tile has an NPC (let NPC click handler work)
      if (tileData?.character) return;

      // Determine primary direction based on which axis has the larger offset
      let direction = null;
      if (Math.abs(dx) > Math.abs(dy)) {
        direction = dx > 0 ? 'E' : 'W';
      } else if (Math.abs(dy) > Math.abs(dx)) {
        direction = dy > 0 ? 'S' : 'N';
      } else {
        direction = dx > 0 ? 'E' : 'W';
      }

      if (navigator.vibrate) navigator.vibrate(15);
      onMove(direction);
      return;
    }

    // v640.7: Non-adjacent click → try BFS auto-path through passable tiles
    if (!tileData || tileData.passable === false) return;
    const m = mazeRef.current;
    const ch = characterRef.current;
    if (!m?.grid || !ch) return;
    const path = findAutoPath(m.grid, ch.x_position, ch.y_position, worldX, worldY);
    if (!path || !path.length) return;
    autoPathQueueRef.current = path;
    setAutoPathTarget({ x: worldX, y: worldY });
    if (navigator.vibrate) navigator.vibrate(15);
    stepAutoPath();
  }, [onMove, cancelAutoPath, stepAutoPath]);

  // Swipe gesture support for the map area
  const handleTouchStart = useCallback((e) => {
    // Any touch input cancels in-flight auto-path
    if (autoPathQueueRef.current.length || autoPathTimerRef.current) {
      cancelAutoPath();
    }
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
  }, [cancelAutoPath]);

  const handleTouchEnd = useCallback((e) => {
    if (!touchStartRef.current || !onMove) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    const elapsed = Date.now() - touchStartRef.current.time;
    touchStartRef.current = null;

    // Only process swipes (distance > 30px, time < 300ms)
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 30 || elapsed > 300) return; // Too short or too slow = tap, not swipe

    // Mark swipe so the click handler doesn't also fire
    swipedRef.current = true;

    let direction;
    if (Math.abs(dx) > Math.abs(dy)) {
      direction = dx > 0 ? 'E' : 'W';
    } else {
      direction = dy > 0 ? 'S' : 'N';
    }

    if (navigator.vibrate) {
      navigator.vibrate(15);
    }
    onMove(direction);
  }, [onMove]);

  if (!character || !maze) {
    return (
      <div className="map-grid">
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
          Loading map...
        </div>
      </div>
    );
  }

  const { x_position: playerX, y_position: playerY } = character;
  const { grid, rows, cols } = maze;

  // Create grid — either full maze or centered on player
  const renderGrid = () => {
    const tiles = [];

    // Full-maze mode: iterate every tile in the maze
    const startY = showFullMaze ? 0 : playerY - halfRows;
    const endY   = showFullMaze ? rows - 1 : playerY + halfRows;
    const startX = showFullMaze ? 0 : playerX - halfCols;
    const endX   = showFullMaze ? cols - 1 : playerX + halfCols;
    
    for (let worldY = startY; worldY <= endY; worldY++) {
      for (let worldX = startX; worldX <= endX; worldX++) {
        const dx = worldX - playerX;
        const dy = worldY - playerY;
        
        // Grid position relative to top-left
        const gridX = showFullMaze ? worldX : dx + halfCols;
        const gridY = showFullMaze ? worldY : dy + halfRows;
        
        // Use percentage-based positioning scaled to viewport dimensions
        const leftPct = `${gridX * tilePctW}%`;
        const topPct  = `${gridY * tilePctH}%`;
        
        // Check maze bounds
        let tileData = null;
        if (worldY >= 0 && worldY < rows && worldX >= 0 && worldX < cols) {
          tileData = grid[worldY] && grid[worldY][worldX];
        }

        // Find players at this position
        const playersHere = nearbyPlayers.filter(p => 
          p.x_position === worldX && p.y_position === worldY
        );

        const isPlayerPosition = dx === 0 && dy === 0;
        const tileImageUrl = tileData && tileData.tile ? `/tiles/${tileData.tile}` : null;
        
        // Check if there are clickable players on this tile (not the player's own tile)
        const hasClickablePlayers = !isPlayerPosition && playersHere.length > 0;

        const tileClass = gridLayout
          ? `m-map-tile${!isPlayerPosition && tileData?.passable !== false ? ' map-tile-tappable' : ''}`
          : `map-tile${!isPlayerPosition && tileData?.passable !== false ? ' map-tile-tappable' : ''}`;

        tiles.push(
          <div
            key={`tile-${worldX}-${worldY}`}
            className={tileClass}
            style={{
              ...(gridLayout ? {} : {
                left: leftPct,
                top: topPct,
                width: `${tilePctW}%`,
                height: `${tilePctH}%`,
              }),
              backgroundColor: tileData ? getTileBackgroundColor(tileData.tile) : '#0a0a0f',
              boxShadow: isPlayerPosition
                ? 'inset 0 0 0 2px rgba(201, 168, 76, 0.6)'
                : (autoPathTarget && autoPathTarget.x === worldX && autoPathTarget.y === worldY
                    ? 'inset 0 0 0 2px rgba(96, 165, 250, 0.85)'
                    : 'none'),
              cursor: !isPlayerPosition && tileData?.passable !== false ? 'pointer' : 'default'
            }}
            title={tileData ? `${tileData.tile || 'Empty'} - ${tileData.passable ? 'passable' : 'blocked'}` : 'Void'}
            onClick={() => handleTileClick(dx, dy, worldX, worldY, tileData)}
          >
            {/* Background tile image */}
            {tileImageUrl && (
              <img 
                src={tileImageUrl}
                alt={tileData.tile}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: gridLayout ? 'fill' : 'cover',
                  zIndex: 1,
                  display: 'block'
                }}
                onError={(e) => {
                  console.error('Failed to load tile image:', tileImageUrl);
                  e.target.style.display = 'none';
                }}
                onLoad={(e) => {
                  // Reset display in case a previous error on a recycled element hid it
                  e.target.style.display = 'block';
                }}
              />
            )}

            {/* Render link indicator */}
            {tileData && tileData.link && (
              <div style={{
                position: 'absolute',
                top: '2px',
                left: '2px',
                width: '8px',
                height: '8px',
                backgroundColor: '#10b981',
                borderRadius: '50%',
                boxShadow: '0 0 4px rgba(16, 185, 129, 0.6)',
                zIndex: 3
              }} title={`Exit to ${tileData.link.maze}`} />
            )}

            {/* Render NPC sprite — prefer grid .npc property (condition-evaluated) over static npcSprites */}
            {(() => {
              // The grid .npc property comes from process commands that were condition-evaluated
              // server-side (e.g., quest NPCs that should only appear when a quest is active).
              // The npcSprites map is a static regex parse and may show NPCs that shouldn't be visible.
              const gridNpc = tileData && tileData.npc;
              const spriteKey = `${worldY}_${worldX}`;
              const staticNpc = maze.npcSprites && maze.npcSprites[spriteKey];
              const npcSprite = gridNpc || staticNpc;

              // Quest indicator for this tile (legacy: string; current: { status, questName, giver })
              const rawQI = maze.questIndicators && maze.questIndicators[`${worldX},${worldY}`];
              const questIndicator = typeof rawQI === 'string' ? rawQI : (rawQI && rawQI.status) || null;
              const questName = (rawQI && typeof rawQI === 'object') ? rawQI.questName : null;
              const questTitle = questName
                ? (questIndicator === 'available' ? `❗ ${questName} — available!` : `❓ ${questName} — in progress`)
                : (questIndicator === 'available' ? 'Quest available!' : 'Quest in progress');
              
              if (npcSprite) {
                // Determine if this is a tile-like image (used as blocking terrain) or a character sprite
                const isTileImage = npcSprite.includes('castle') || npcSprite.includes('cave') || 
                                    npcSprite.includes('wall') || npcSprite.includes('plains') || 
                                    npcSprite.includes('dark0') || npcSprite.includes('tower') ||
                                    npcSprite.includes('water') || npcSprite.includes('inside');
                if (!isTileImage) {
                  return (
                    <>
                      <img
                        src={`/images/sprites/${npcSprite}`}
                      alt="NPC"
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '70%',
                        height: '70%',
                        objectFit: 'contain',
                        zIndex: 5,
                        imageRendering: 'pixelated',
                        filter: 'drop-shadow(0 0 3px rgba(245, 158, 11, 0.6))'
                      }}
                      onError={(e) => {
                        // Hide the broken image — React will still render the fallback dot below
                        // IMPORTANT: Do NOT remove/create DOM elements here — that corrupts React's
                        // virtual DOM reconciliation and causes rendering glitches during rapid movement
                        e.target.style.display = 'none';
                      }}
                    />
                      {questIndicator && (
                        <div className={`quest-indicator quest-indicator-${questIndicator}`}
                          title={questTitle}
                        >
                          {questIndicator === 'available' ? '❗' : '❓'}
                        </div>
                      )}
                    </>
                  );
                }
              }
              // Show a generic NPC indicator for tiles that have a character/npc but no renderable sprite
              if (tileData && (tileData.character || tileData.npc) && !npcSprite) {
                return (
                  <>
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '14px',
                      height: '14px',
                      backgroundColor: '#f59e0b',
                      borderRadius: '50%',
                      border: '2px solid #fbbf24',
                      boxShadow: '0 0 6px rgba(245, 158, 11, 0.6)',
                      zIndex: 5
                    }} />
                    {questIndicator && (
                      <div className={`quest-indicator quest-indicator-${questIndicator}`}
                        title={questTitle}
                      >
                        {questIndicator === 'available' ? '❗' : '❓'}
                      </div>
                    )}
                  </>
                );
              }
              return null;
            })()}

            {/* Render shop indicator */}
            {tileData && tileData.shop && (
              <div style={{
                position: 'absolute',
                bottom: '2px',
                left: '2px',
                width: '10px',
                height: '10px',
                backgroundColor: '#c9a84c',
                borderRadius: '2px',
                boxShadow: '0 0 4px rgba(201, 168, 76, 0.6)',
                zIndex: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '7px'
              }} title="Shop">
                $
              </div>
            )}

            {/* Render market indicator */}
            {tileData && tileData.market && (
              <div style={{
                position: 'absolute',
                bottom: '2px',
                right: '2px',
                width: '12px',
                height: '12px',
                backgroundColor: '#e67e22',
                borderRadius: '2px',
                boxShadow: '0 0 5px rgba(230, 126, 34, 0.6)',
                zIndex: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '8px',
                color: '#fff',
                fontWeight: 'bold'
              }} title="Market  Player Auction House">
                </div>
            )}

            {/* Render arena indicator */}
            {tileData && tileData.arena && (
              <div style={{
                position: 'absolute',
                bottom: '2px',
                right: '2px',
                width: '12px',
                height: '12px',
                backgroundColor: '#c0392b',
                borderRadius: '2px',
                boxShadow: '0 0 5px rgba(192, 57, 43, 0.6)',
                zIndex: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '8px',
                color: '#fff',
                fontWeight: 'bold'
              }} title="Arena  PvP Combat">
                </div>
            )}

            {/* Render stash indicator */}
            {tileData && tileData.stash && (
              <div style={{
                position: 'absolute',
                bottom: '2px',
                left: '2px',
                width: '12px',
                height: '12px',
                backgroundColor: '#8e44ad',
                borderRadius: '2px',
                boxShadow: '0 0 5px rgba(142, 68, 173, 0.6)',
                zIndex: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '8px',
                color: '#fff',
                fontWeight: 'bold'
              }} title="Inn Stash — Storage">
                📦</div>
            )}

            {/* Render Delve Entrance overlay image */}
            {tileData && tileData.delve && (
              <img
                src="/tiles/delve-entrance.png"
                alt="Delve Entrance"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  zIndex: 4,
                  pointerEvents: 'none',
                }}
                title="⚔️ Delve Entrance — Brave the dungeon within"
              />
            )}

            {/* Render Campfire animated sprite */}
            {tileData && tileData.campfire && (
              <img
                src="/tiles/campfire.gif"
                alt="Campfire"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  zIndex: 5,
                  pointerEvents: 'none',
                  filter: 'drop-shadow(0 0 4px rgba(255, 120, 0, 0.7))',
                }}
                title="🔥 Campfire — Rest here to restore HP/MP"
              />
            )}

            {/* Render Treasure Map destination indicator */}
            {maze.treasureMapTile && maze.treasureMapTile.x === worldX && maze.treasureMapTile.y === worldY && (() => {
              const tmr = maze.treasureMapTile.rarity || 'common';
              const glowColor = { common: 'rgba(255, 215, 0, 0.9)', rare: 'rgba(168, 85, 247, 0.9)', legendary: 'rgba(239, 68, 68, 0.9)' }[tmr];
              return (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 8,
                  fontSize: '18px',
                  filter: `drop-shadow(0 0 6px ${glowColor})`,
                  animation: `treasure-pulse-${tmr} 2s ease-in-out infinite`,
                  pointerEvents: 'none',
                }} title={`🗺️ ${tmr === 'legendary' ? 'Legendary' : tmr === 'rare' ? 'Rare' : ''} Treasure Map Destination`}>
                  {tmr === 'legendary' ? '🌟' : tmr === 'rare' ? '✨' : '🗺️'}
                </div>
              );
            })()}

            {/* Render clickable NPC overlay for adjacent tiles */}
            {tileData && tileData.character && Math.abs(dx) <= 1 && Math.abs(dy) <= 1 && !(dx === 0 && dy === 0) && (
              <div 
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '24px',
                  height: '24px',
                  backgroundColor: 'rgba(245, 158, 11, 0.7)',
                  borderRadius: '50%',
                  border: '2px solid #f59e0b',
                  zIndex: 4,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  color: '#fff',
                  fontWeight: 'bold'
                }} 
                title={`Talk to ${tileData.character}`}
                onClick={() => onNpcClick && onNpcClick(tileData.character)}
              >
                </div>
            )}

            {/* Render player sprite */}
            {isPlayerPosition && (
              <PlayerAvatar 
                character={character}
                direction={character.direction || 'S'}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 10
                }}
                title={`${character.name} (You)`}
              />
            )}
            
            {/* Render other players — clickable to inspect */}
            {playersHere.map((player, index) => (
              <div
                key={player.name}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 9 + index,
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!onPlayerClick) return;
                  if (playersHere.length === 1) {
                    // Single player — inspect directly
                    onPlayerClick(player.id);
                  } else {
                    // Multiple players — show selection popup
                    const rect = e.currentTarget.closest('.map-tile').getBoundingClientRect();
                    const mapRect = mapRef.current?.getBoundingClientRect();
                    setPlayerSelectPopup({
                      players: playersHere,
                      x: rect.left - (mapRect?.left || 0) + rect.width / 2,
                      y: rect.top - (mapRect?.top || 0)
                    });
                  }
                }}
                title={`Click to inspect ${player.name}`}
              >
                <PlayerAvatar
                  character={player}
                  direction={player.direction || 'S'}
                  style={{
                    transform: 'scale(0.75)',
                    opacity: 0.85,
                    filter: 'drop-shadow(0 0 3px rgba(96, 165, 250, 0.7))',
                  }}
                />
              </div>
            ))}
          </div>
        );
      }
    }
    
    return tiles;
  };

  return (
    <div 
      className={gridLayout ? 'm-map-grid' : 'map-grid'}
      ref={mapRef}
      style={gridLayout ? {
        display: 'grid',
        gridTemplateColumns: `repeat(${showFullMaze ? maze.cols : viewCols}, 1fr)`,
        gridTemplateRows: `repeat(${showFullMaze ? maze.rows : viewRows}, 1fr)`,
      } : undefined}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {renderGrid()}

      {/* Multi-player selection popup */}
      {playerSelectPopup && (
        <div 
          className="player-select-overlay"
          onClick={() => setPlayerSelectPopup(null)}
        >
          <div 
            className="player-select-popup"
            onClick={(e) => e.stopPropagation()}
            style={{
              left: `${Math.min(Math.max(playerSelectPopup.x, 80), mapRef.current?.offsetWidth - 80 || 200)}px`,
              top: `${Math.max(playerSelectPopup.y - 10, 10)}px`
            }}
          >
            <div className="player-select-header">
              <span>👥 Select Player</span>
              <button className="player-select-close" onClick={() => setPlayerSelectPopup(null)}>✕</button>
            </div>
            {playerSelectPopup.players.map((player) => (
              <div 
                key={player.id}
                className="player-select-row"
                onClick={() => {
                  if (onPlayerClick) onPlayerClick(player.id);
                  setPlayerSelectPopup(null);
                }}
              >
                <span className="player-select-name">{player.name}</span>
                <span className="player-select-info">
                  Lv.{player.level} {player.advanced_class || player.class}
                  {player.guild_tag ? ` [${player.guild_tag}]` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to get fallback tile colors based on tile filename
const getTileBackgroundColor = (tileFilename) => {
  if (!tileFilename) return '#000';
  
  const tile = tileFilename.toLowerCase();
  
  if (tile.includes('grass') || tile.includes('plains')) return '#4ade80';
  if (tile.includes('forest')) return '#16a34a';
  if (tile.includes('stone') || tile.includes('cobble')) return '#64748b';
  if (tile.includes('water') || tile.includes('river')) return '#3b82f6';
  if (tile.includes('desert') || tile.includes('sand')) return '#eab308';
  if (tile.includes('building') || tile.includes('wall')) return '#374151';
  if (tile.includes('castle')) return '#6366f1';
  if (tile.includes('cave') || tile.includes('dungeon')) return '#1f2937';
  if (tile.includes('jungle')) return '#059669';
  if (tile.includes('immigration')) return '#8b5cf6';
  
  return '#333'; // Default color
};

export default MapGrid;
